import { DoubleDocumentsIcon } from "@components/Icons";
import { useTheme } from "@hooks/useTheme";
import SelectAllIcon from "../Icons/SelectAllIcon";
import { useTranslation } from "react-i18next";

const DualSelectBox = ({
  availableItems = [],
  selectedItems = [],
  onMoveToSelected,
  onMoveToAvailable,
  onSelectAllAvailable,
  onSelectAllSelected,
  selectedAvailableItems = [],
  selectedSelectedItems = [],
  onAvailableSelectionChange,
  onSelectedSelectionChange,
  disabled = false,
  height = "200px",
  canEdit = true,
  "data-testid": dataTestId = "dual-select-box",
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();

  // Calculate the list height based on the total height minus control area
  const listHeight = `calc(${height} - 60px)`;

  const handleAvailableItemClick = (item) => {
    if (disabled) return;

    const isSelected = selectedAvailableItems.some((selected) => selected.value === item.value);
    if (isSelected) {
      onAvailableSelectionChange(selectedAvailableItems.filter((selected) => selected.value !== item.value));
    } else {
      onAvailableSelectionChange([...selectedAvailableItems, item]);
    }
  };

  const handleSelectedItemClick = (item) => {
    if (disabled) return;

    const isSelected = selectedSelectedItems.some((selected) => selected.value === item.value);
    if (isSelected) {
      onSelectedSelectionChange(selectedSelectedItems.filter((selected) => selected.value !== item.value));
    } else {
      onSelectedSelectionChange([...selectedSelectedItems, item]);
    }
  };

  const isAvailableItemSelected = (item) => {
    return selectedAvailableItems.some((selected) => selected.value === item.value);
  };

  const isSelectedItemSelected = (item) => {
    return selectedSelectedItems.some((selected) => selected.value === item.value);
  };

  const isAllAvailableSelected = availableItems.length > 0 && selectedAvailableItems.length === availableItems.length;
  const isAllSelectedItemsSelected = selectedItems.length > 0 && selectedSelectedItems.length === selectedItems.length;

  return (
    <div className="grid grid-cols-5 gap-4" data-testid={dataTestId}>

      {/* Selected Items - Left Column */}
      <div className="col-span-2 space-y-2" data-testid={`${dataTestId}-selected-column`}>
        <h3 className={`text-sm font-medium ${colors.textPrimary}`} data-testid={`${dataTestId}-selected-heading`}>{t("sbom.selected-items")} ({selectedItems.length}) <span className={colors.textDanger}>*</span></h3>
        <div className={`border rounded-lg ${colors.borderPrimary} ${colors.bgInput}`} style={{ height }} data-testid={`${dataTestId}-selected-panel`}>
          {/* Select All Control - Inside the box above the list */}
          <div className="p-2 flex-shrink-0">
            <button
              type="button"
              data-testid={`${dataTestId}-select-all-selected`}
              onClick={canEdit && onSelectAllSelected}
              disabled={disabled || selectedItems.length === 0}
              className={`flex items-center space-x-2 text-base px-2 py-1 rounded transition-colors ${
                selectedItems.length === 0 || disabled ? "cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              <div className="flex items-center">
                <SelectAllIcon isActive={isAllSelectedItemsSelected} className="mr-2" />
                <span
                  className={`font-semibold font-600 ${isAllSelectedItemsSelected ? colors.textActive : colors.textSelectAllInactive}`}
                >
                  {t("sbom.select-all")}
                </span>
              </div>
            </button>
          </div>

          {/* Item List - Takes remaining height */}
          <div
            className="flex-1 overflow-y-auto p-2 space-y-0.5"
            style={{ height: listHeight }}
            data-testid={`${dataTestId}-selected-list`}
          >
            {(!selectedItems || selectedItems.length === 0) ? (
              <div className={`flex flex-col items-center justify-center h-full ${colors.iconMuted}`} data-testid={`${dataTestId}-selected-empty`}>
                <DoubleDocumentsIcon className="w-8 h-8 mb-2" />
                <span className="text-sm">{t("sbom.no-item-available-to-show")}</span>
              </div>
            ) : (
              selectedItems.map((item) => (
                <div
                  key={item.value}
                  data-testid={`${dataTestId}-selected-item-${item.value}`}
                  onClick={canEdit ? () => handleSelectedItemClick(item) : undefined}
                  className={`flex items-center justify-between p-2 cursor-pointer transition-colors ${
                    isSelectedItemSelected(item) ? `${colors.textNormal} ${colors.bgSelectedItem} rounded-xl` : `${colors.hoverBgSelectedRow} rounded-xl`
                  } ${colors.textPrimary}`}
                  style={{
                    backgroundColor: isSelectedItemSelected(item) ? undefined : "transparent",
                  }}
                >
                  <span className="text-sm">{item.label}</span>
                  {isSelectedItemSelected(item) && (
                    <svg className={`w-4 h-4 ${colors.textNormal}`} fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Move Buttons - Center Column */}
      {canEdit ? (
        <div className="flex flex-col items-center justify-center space-y-2" data-testid={`${dataTestId}-move-controls`}>
          <button
            type="button"
            data-testid={`${dataTestId}-move-to-selected`}
            onClick={onMoveToSelected}
            disabled={disabled || selectedAvailableItems.length === 0}
            className={`w-10 h-10 rounded-xl transition-all duration-200 flex items-center justify-center ${
              selectedAvailableItems.length === 0 || disabled
                ? `${colors.bgMoveDisabled} cursor-not-allowed`
                : `${colors.buttonMoveGradient} ${colors.buttonMoveGradientHover} shadow-lg hover:shadow-xl`
            }`}
          >
            <svg className={`w-5 h-5 ${colors.iconOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <button
            type="button"
            data-testid={`${dataTestId}-move-to-available`}
            onClick={onMoveToAvailable}
            disabled={disabled || selectedSelectedItems.length === 0}
            className={`w-10 h-10 rounded-xl transition-all duration-200 flex items-center justify-center ${
              selectedSelectedItems.length === 0 || disabled
                ? `${colors.bgMoveDisabled} cursor-not-allowed`
                : `${colors.buttonMoveGradient} ${colors.buttonMoveGradientHover} shadow-lg hover:shadow-xl`
            }`}
          >
            <svg className={`w-5 h-5 ${colors.iconOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center space-y-2" data-testid={`${dataTestId}-move-controls-placeholder`}></div>
      )}

      {/* Available Items - Right Column */}
      <div className="col-span-2 space-y-2" data-testid={`${dataTestId}-available-column`}>
        <h3 className={`text-sm font-medium ${colors.textPrimary}`} data-testid={`${dataTestId}-available-heading`}>{t("sbom.available-items")} ({availableItems.length})</h3>
        <div className={`border rounded-lg ${colors.borderPrimary} ${colors.bgInput}`} style={{ height }} data-testid={`${dataTestId}-available-panel`}>
          {/* Select All Control - Inside the box above the list */}
          <div className="p-2 flex-shrink-0">
            <button
              type="button"
              data-testid={`${dataTestId}-select-all-available`}
              onClick={canEdit && onSelectAllAvailable}
              disabled={disabled || availableItems.length === 0}
              className={`flex items-center space-x-2 text-base px-2 py-1 rounded transition-colors ${
                availableItems.length === 0 || disabled ? "cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              <div className="flex items-center">
                <SelectAllIcon isActive={isAllAvailableSelected} className="mr-2" />
                <span
                  className={`font-semibold font-600 ${isAllAvailableSelected ? colors.textActive : colors.textSelectAllInactive}`}
                >
                  {t("sbom.select-all")}
                </span>
              </div>
            </button>
          </div>

          {/* Item List - Takes remaining height */}
          <div
            className="flex-1 overflow-y-auto p-2 space-y-0.5"
            style={{ height: listHeight }}
            data-testid={`${dataTestId}-available-list`}
          >
            {availableItems.map((item) => (
              <div
                key={item.value}
                data-testid={`${dataTestId}-available-item-${item.value}`}
                onClick={canEdit ? () => handleAvailableItemClick(item) : undefined}
                className={`flex items-center justify-between p-2 cursor-pointer transition-colors ${
                    isAvailableItemSelected(item) ? `${colors.textNormal} ${colors.bgSelectedItem} rounded-xl` : `${colors.hoverBgSelectedRow} rounded-xl`
                } ${colors.textPrimary}`}
                style={{
                    backgroundColor: isAvailableItemSelected(item) ? undefined : "transparent",
                }}
              >
                  <span className="text-sm">{item.label}</span>
                {isAvailableItemSelected(item) && (
                  <svg className={`w-4 h-4 ${colors.textNormal}`} fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DualSelectBox;
